import { useRequiredContext } from "@redotech/react-util/context";
import { toast } from "@redotech/redo-web/alert";
import { fileToBase64 } from "@redotech/redo-web/image-upload-utils";
import { useMutation } from "@tanstack/react-query";
import { ChangeEvent, useCallback, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";
import { RedoAdminClient } from "../../../../client";
import {
  uploadReamazeMacros,
  uploadZendeskMacros,
} from "../../../../client/team";
import { ImportPlatform } from "./types";

interface UseMacroImportOptions {
  teamId: string | undefined;
  platform: ImportPlatform;
  client: RedoAdminClient;
}

export function useMacroImport({
  teamId,
  platform,
  client,
}: UseMacroImportOptions) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [file, setFile] = useState<File | null>(null);

  const mutation = useMutation({
    mutationFn: async (fileToUpload: File) => {
      if (!teamId) {
        throw new Error("Team ID is required");
      }

      switch (platform) {
        case ImportPlatform.ZENDESK:
          return uploadZendeskMacros(client, { teamId, file: fileToUpload });

        case ImportPlatform.REAMAZE:
          return uploadReamazeMacros(client, { teamId, file: fileToUpload });

        case ImportPlatform.RICHPANEL: {
          const fileContent = await fileToBase64(fileToUpload);
          return rpcClient.uploadRichPanelMacros({
            teamId,
            fileName: fileToUpload.name,
            fileContent,
            mimeType: fileToUpload.type || "text/csv",
          });
        }

        case ImportPlatform.GORGIAS: {
          const fileContent = await fileToBase64(fileToUpload);
          return rpcClient.uploadGorgiasMacros({
            teamId,
            fileName: fileToUpload.name,
            fileContent,
            mimeType: fileToUpload.type || "text/csv",
          });
        }

        case ImportPlatform.GORGIAS_NATIVE: {
          const fileContent = await fileToBase64(fileToUpload);
          return rpcClient.uploadGorgiasNativeMacros({
            teamId,
            fileName: fileToUpload.name,
            fileContent,
            mimeType: fileToUpload.type || "text/csv",
          });
        }

        default:
          throw new Error(`Unknown platform: ${platform}`);
      }
    },
    onSuccess: (result) => {
      const platformName = getPlatformDisplayName(platform);
      if (result && "importedCount" in result) {
        toast(
          `${platformName} macros uploaded successfully: ${result.importedCount} imported, ${result.skippedCount} skipped`,
          { variant: "success" },
        );
      } else {
        toast(`${platformName} macros uploaded successfully`, {
          variant: "success",
        });
      }
      setFile(null);
    },
    onError: (error) => {
      const platformName = getPlatformDisplayName(platform);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast(`Failed to upload ${platformName} macros: ${errorMessage}`, {
        variant: "error",
      });
    },
  });

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files[0]) {
        setFile(files[0]);
      }
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    if (file && teamId) {
      mutation.mutate(file);
    }
  }, [file, teamId, mutation]);

  return {
    file,
    setFile,
    loading: mutation.isPending,
    handleFileChange,
    handleSubmit,
  };
}

function getPlatformDisplayName(platform: ImportPlatform): string {
  switch (platform) {
    case ImportPlatform.GORGIAS:
      return "Gorgias";
    case ImportPlatform.GORGIAS_NATIVE:
      return "Gorgias Native";
    case ImportPlatform.ZENDESK:
      return "Zendesk";
    case ImportPlatform.REAMAZE:
      return "Reamaze";
    case ImportPlatform.RICHPANEL:
      return "RichPanel";
  }
}
